import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bookmark, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

// Named presets of columns + sort (+ filters where the table has them), saved per
// user/company/table. Switching a view applies its layout/sort/filters instantly.
export default function SavedViews({ tableId, prefs, filters = {}, onApplyView, systemViews = [], initialActiveId }) {
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(initialActiveId || null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setActiveId(initialActiveId || null);
    api.get(`/me/table-views/${tableId}`).then((r) => setViews(r.data?.views || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  const applyView = (v) => { setActiveId(v.id); onApplyView?.(v.prefs, v.filters, v); };
  const save = async () => {
    const nm = name.trim();
    if (!nm) return;
    setSaving(true);
    try {
      const r = await api.post(`/me/table-views/${tableId}`, { name: nm, prefs, filters });
      setViews((vs) => [...vs, r.data.view]);
      setActiveId(r.data.view.id);
      setSaveOpen(false); setName("");
      toast.success(`View “${nm}” saved`);
    } catch {
      toast.error("Could not save view");
    } finally { setSaving(false); }
  };
  const del = (id) => {
    setViews((vs) => vs.filter((v) => v.id !== id));
    if (activeId === id) setActiveId(null);
    api.delete(`/me/table-views/${tableId}/${id}`).catch(() => {});
  };

  const active = [...systemViews, ...views].find((v) => v.id === activeId);
  const hasFilters = filters && Object.keys(filters).some((k) => {
    const val = filters[k];
    return val && (typeof val !== "object" || Object.keys(val).length);
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid={`${tableId}-views-button`}>
            <Bookmark className="w-4 h-4 text-slate-600" />
            <span className="max-w-[120px] truncate">{active ? active.name : "Views"}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {systemViews.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs">Built-in</DropdownMenuLabel>
              {systemViews.map((v) => (
                <DropdownMenuItem key={v.id} onClick={() => applyView(v)} data-testid={`${tableId}-sysview-${v.id}`} className="gap-2">
                  {activeId === v.id ? <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" /> : <span className="w-3.5 shrink-0" />}
                  <span className="flex-1 truncate">{v.name}</span>
                  <span className="text-[10px] font-medium text-slate-400 shrink-0">Built-in</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel className="text-xs">Saved views</DropdownMenuLabel>
          {views.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No saved views yet</div>}
          {views.map((v) => (
            <DropdownMenuItem key={v.id} onClick={() => applyView(v)} data-testid={`${tableId}-view-${v.id}`} className="gap-2">
              {activeId === v.id ? <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" /> : <span className="w-3.5 shrink-0" />}
              <span className="flex-1 truncate">{v.name}</span>
              <button onClick={(e) => { e.stopPropagation(); del(v.id); }} data-testid={`${tableId}-view-delete-${v.id}`}
                className="text-slate-300 hover:text-rose-500 p-0.5 shrink-0" title="Delete view"><Trash2 className="w-3.5 h-3.5" /></button>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSaveOpen(true)} data-testid={`${tableId}-view-save`}>
            <Plus className="w-4 h-4 mr-2 text-slate-500" /> Save current as…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid={`${tableId}-view-save-dialog`}>
          <DialogHeader><DialogTitle>Save current view</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Saves the current columns and sort{hasFilters ? ", plus the active filters," : ""} as a named preset.</p>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My installs this week"
            onKeyDown={(e) => e.key === "Enter" && save()} data-testid={`${tableId}-view-name-input`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={saving || !name.trim()} onClick={save} data-testid={`${tableId}-view-save-confirm`}>Save view</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
